import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { HttpService } from '@app/core/http/http.service';
import { Credentials, CredentialsService } from './credentials.service';

import { ActivatedRoute, Router } from '@angular/router';
import { SCData } from '@app/models/core/SCData';
import { map, tap } from 'rxjs/operators';
import { BaseService } from '../helperclasses';
import { PosthogService } from '../posthog.service';
import posthog from 'posthog-js';

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

export interface ResetPasswordContext {
  email: string;
}

export interface ResetPasswordMessage {
  message: string;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forceLogout: BehaviorSubject<any> = new BehaviorSubject(undefined);
  currentVideoId!: string | undefined;

  constructor(
    private credentialsService: CredentialsService,
    private http: HttpService,
    private router: Router,
    private route: ActivatedRoute,
    private posthogService: PosthogService,
  ) {
    super();
    if (
      this.route.snapshot.children[0]?.data &&
      this.route.snapshot.children[0]?.data['authentication'] !== false
    ) {
      if (this.credentialsService.credentials) {
        this.getCurrentUser()
          .pipe(
            tap((_res) => {
              if (this.credentialsService.credentials?.id) {
                const creds = this.credentialsService.credentials;
                console.warn(creds);
                this.posthogService
                  .ensureInitialized()
                  .then(() => {
                    posthog.identify(creds.id, {
                      email: creds.email,
                      name: creds.username,
                    });
                  })
                  .catch((err) =>
                    console.warn(
                      '[PostHog] init failed; identify skipped:',
                      err,
                    ),
                  );
              } else {
                console.warn('[PostHog] credentials not set');
              }
            }),
          )
          .subscribe();
      }
    }
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<SCData<Credentials>> {
    return this.loginHelper(context).pipe(
      map((res) => {
        this.credentialsService.setCredentials(res.data, context.remember);
        this.getCurrentUser()
          .pipe(
            tap((_res) => {
              if (this.credentialsService.credentials?.id) {
                const creds = this.credentialsService.credentials;
                console.warn(creds);
                this.posthogService
                  .ensureInitialized()
                  .then(() => {
                    posthog.identify(creds.id, {
                      email: creds.email,
                      name: creds.username,
                    });
                  })
                  .catch((err) =>
                    console.warn(
                      '[PostHog] init failed; identify skipped:',
                      err,
                    ),
                  );
              } else {
                console.warn('[PostHog] credentials not set');
              }
            }),
          )
          .subscribe();
        return res;
      }),
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(forceLogout = false) {
    // Prepare the URL with query parameters if currentVideoId is available
    const apiUrl =
      this.currentVideoId && forceLogout
        ? `/course-auth/logout?video_id=${encodeURIComponent(this.currentVideoId)}`
        : '/course-auth/logout';
    // Customize credentials invalidation here
    this.addSubscription(this.http.post(apiUrl, undefined)).subscribe(() => {
      this.currentVideoId = undefined;
      this.credentialsService.setCredentials();
      this.router.navigate(['/login']);
    });
  }

  loginHelper(context: LoginContext): Observable<SCData<Credentials>> {
    return <Observable<SCData<Credentials>>>(
      this.http.post('/course-auth/login', context)
    );
  }

  getCurrentUser() {
    const $currentUser = this.getCurrentUserHelper();
    $currentUser.subscribe(
      (res) => {
        res.data.access_token = this.credentialsService.token;
        this.credentialsService.setCredentials(
          res.data,
          this.credentialsService.remember,
        );
      },
      () => {
        if (!this.credentialsService.credentials?.id) {
          this.credentialsService.setCredentials();
          this.router.navigate(['/login'], {
            queryParams: { redirect: this.router.url },
            replaceUrl: true,
          });
        }
      },
    );

    return $currentUser;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCurrentUserHelper(): Observable<any> {
    return <Observable<Credentials>>this.http.get('/course-auth/me');
  }

  resetPasswordRequest(context: ResetPasswordContext) {
    return <Observable<SCData<ResetPasswordMessage>>>(
      this.http.post('/course-auth/reset-password', context)
    );
  }

  resetPassword(context: ResetPasswordContext) {
    return <Observable<SCData<ResetPasswordMessage>>>(
      this.http.patch('/course-auth/reset-password', context)
    );
  }

  $forceLogout() {
    return this.forceLogout.asObservable();
  }
}
